/**
 * The §16.3 state × display matrix, as data (AC-026, AC-032).
 *
 * §16.3 is a table with eleven rows and three columns: for each runtime state it
 * fixes what the Team Overview shows, what the agent card shows, and what the
 * Office shows. Scattering that across screens is how the three drift apart —
 * and the specific drift the spec is defending against is a broken source
 * rendering as a calm `idle` somewhere (AC-026).
 *
 * So the matrix lives here, once, as pure data plus pure functions that turn an
 * `OfficeAgent` into the *content* each column requires. The screens render it;
 * they do not decide it.
 *
 * The eleven states are exactly `AgentDisplayState` from `ui-projections`, which
 * is deliberate: the projection layer and the presentation layer enumerate the
 * same set, so a new state cannot appear in the projection without breaking
 * `DISPLAY_MATRIX`'s exhaustive `Record` at compile time.
 */

import type { AgentDisplayState, OfficeAgent, OfficeScene } from '@autonomous-team/ui-projections';

import { formatDurationMs, formatAgeMs, freshnessLabel, pluralize, NOT_APPLICABLE } from './format.js';

/**
 * Visual tone. Drives colour *only*.
 *
 * §16.5 forbids colour from being the sole indicator, so every tone is always
 * accompanied by `badgeText` and a labelled `icon` from the same spec.
 */
export type Tone = 'neutral' | 'info' | 'active' | 'wait' | 'attention' | 'unknown' | 'danger';

export interface DisplaySpec {
  readonly state: AgentDisplayState;
  /** The §16.3 row name, so the UI and the spec can be read side by side. */
  readonly matrixRow: string;
  /** Always rendered next to the colour. Never an empty string. */
  readonly badgeText: string;
  /** A glyph — decorative on its own, hence always paired with `iconLabel`. */
  readonly icon: string;
  /** The accessible name for the glyph. */
  readonly iconLabel: string;
  readonly tone: Tone;
  /** What the §16.3 "Team Overview" column requires. */
  readonly teamOverviewRequirement: string;
  /** What the §16.3 "Agent card" column requires. */
  readonly agentCardRequirement: string;
  /** What the §16.3 "Office" column requires. */
  readonly officeRequirement: string;
  /**
   * Whether the Office may animate in this state.
   *
   * `false` for every state where the facts are not current: AC-026 requires the
   * factual scene to *freeze*, and an animation is a claim that time is passing
   * in a way we can still observe.
   */
  readonly officeAnimates: boolean;
}

/**
 * The matrix itself.
 *
 * Ordered as §16.3 orders it so the two can be diffed by eye.
 */
export const DISPLAY_MATRIX: Readonly<Record<AgentDisplayState, DisplaySpec>> = {
  loading: {
    state: 'loading',
    matrixRow: 'Loading',
    badgeText: 'LOADING',
    icon: '◌',
    iconLabel: 'loading',
    tone: 'neutral',
    teamOverviewRequirement: 'skeleton placeholders plus the name of the source being read',
    agentCardRequirement: 'skeleton placeholder',
    officeRequirement: 'scene hidden, loading banner shown',
    officeAnimates: false,
  },
  empty: {
    state: 'empty',
    matrixRow: 'Empty',
    badgeText: 'NOT CONFIGURED',
    icon: '○',
    iconLabel: 'nothing configured',
    tone: 'neutral',
    teamOverviewRequirement: 'setup call to action',
    agentCardRequirement: 'no roles configured',
    officeRequirement: 'empty office with a setup call to action',
    officeAnimates: false,
  },
  idle: {
    state: 'idle',
    matrixRow: 'Healthy idle',
    badgeText: 'IDLE',
    icon: '●',
    iconLabel: 'idle',
    tone: 'neutral',
    teamOverviewRequirement: 'queue depths and last sync time',
    agentCardRequirement: 'idle, with when it was last seen',
    officeRequirement: 'break decoration, labelled cosmetic',
    officeAnimates: true,
  },
  queued: {
    state: 'queued',
    matrixRow: 'Queued',
    badgeText: 'QUEUED',
    icon: '▤',
    iconLabel: 'queued',
    tone: 'info',
    teamOverviewRequirement: 'count of waiting tasks',
    agentCardRequirement: 'queued',
    officeRequirement: 'the task book waits outside the desk',
    officeAnimates: true,
  },
  delivering: {
    state: 'delivering',
    matrixRow: 'Delivering',
    badgeText: 'DELIVERING',
    icon: '↦',
    iconLabel: 'delivering',
    tone: 'info',
    teamOverviewRequirement: 'the attempt identifier',
    agentCardRequirement: 'delivering',
    officeRequirement: 'the character walks, and only after a dispatch receipt',
    officeAnimates: true,
  },
  working: {
    state: 'working',
    matrixRow: 'Running',
    badgeText: 'WORKING',
    icon: '▶',
    iconLabel: 'working',
    tone: 'active',
    teamOverviewRequirement: 'stage and elapsed time',
    agentCardRequirement: 'working',
    officeRequirement: 'the character sits at the desk, and only after a run-start receipt',
    officeAnimates: true,
  },
  'waiting-tool': {
    state: 'waiting-tool',
    matrixRow: 'Waiting tool',
    badgeText: 'WAITING ON TOOL',
    icon: '⧗',
    iconLabel: 'waiting for a tool',
    tone: 'wait',
    teamOverviewRequirement: 'the tool and its timeout',
    agentCardRequirement: 'waiting-tool',
    officeRequirement: 'a factual badge only; no invented activity',
    officeAnimates: false,
  },
  'waiting-human': {
    state: 'waiting-human',
    matrixRow: 'Waiting human',
    badgeText: 'WAITING ON HUMAN',
    icon: '✋',
    iconLabel: 'waiting for a human decision',
    tone: 'attention',
    teamOverviewRequirement: 'the decision that was requested',
    agentCardRequirement: 'waiting-human',
    officeRequirement: 'the character stands at the Human Review mailbox',
    officeAnimates: false,
  },
  blocked: {
    state: 'blocked',
    matrixRow: 'Blocked',
    badgeText: 'BLOCKED',
    icon: '⛔',
    iconLabel: 'blocked',
    tone: 'danger',
    teamOverviewRequirement: 'the reason and the recovery action',
    agentCardRequirement: 'blocked',
    officeRequirement: 'a red factual badge',
    officeAnimates: false,
  },
  unknown: {
    state: 'unknown',
    matrixRow: 'Partial outage',
    badgeText: 'UNKNOWN',
    icon: '?',
    iconLabel: 'state unknown because a source is stale',
    tone: 'unknown',
    teamOverviewRequirement: 'the cached state together with how stale it is',
    agentCardRequirement: 'unknown, because the source is stale',
    officeRequirement: 'the scene freezes and a stale overlay is drawn over it',
    officeAnimates: false,
  },
  error: {
    state: 'error',
    matrixRow: 'Error',
    badgeText: 'ERROR',
    icon: '✕',
    iconLabel: 'error',
    tone: 'danger',
    teamOverviewRequirement: 'the source-specific error and a retry control',
    agentCardRequirement: 'the error detail',
    officeRequirement: 'never a false idle',
    officeAnimates: false,
  },
};

/** Every state, in §16.3 order. Used by the Truth View's matrix reference table. */
export const MATRIX_ORDER: readonly AgentDisplayState[] = [
  'loading',
  'empty',
  'idle',
  'queued',
  'delivering',
  'working',
  'waiting-tool',
  'waiting-human',
  'blocked',
  'unknown',
  'error',
];

export function displaySpec(state: AgentDisplayState): DisplaySpec {
  return DISPLAY_MATRIX[state];
}

/** A label/value pair with an optional screen-reader elaboration. */
export interface DetailLine {
  readonly label: string;
  readonly value: string;
  /** Extra sentence announced by a screen reader when the value needs context. */
  readonly announce: string | null;
}

function line(label: string, value: string, announce: string | null = null): DetailLine {
  return { label, value, announce };
}

/**
 * The Team Overview column of §16.3 for one role.
 *
 * Each branch returns the specific facts the matrix names for that row — the
 * point of the table is that a `queued` role shows a *count* and a `working`
 * role shows *stage, elapsed and spend*, not that both show a generic status.
 */
export function teamOverviewDetails(agent: OfficeAgent): readonly DetailLine[] {
  switch (agent.state) {
    case 'loading':
    case 'empty':
      return [line('Source', 'control plane projection')];
    /*
     * An idle role gets its queue depth and nothing else.
     *
     * "Last sync: never observed" and "Last heartbeat: never" were on every card
     * of an idle team — three quarters of the roster reading as though something
     * had failed. A heartbeat only exists while a role holds a lease (§8.3.6):
     * for a role that has not claimed anything there is no such fact, and
     * printing its absence twice invented a problem.
     */
    case 'idle':
      return [line('Queue', pluralize(agent.queueDepth, 'task'))];
    case 'queued':
      return [
        line('Waiting tasks', String(agent.queueDepth)),
        line('Dispatched', 'nothing yet', 'The queue is non-empty but no attempt has been dispatched.'),
      ];
    case 'delivering':
      return [
        line('Attempt', agent.attemptId.value ?? 'unknown', agent.stateExplanation),
        line('Stage', agent.currentStageId ?? NOT_APPLICABLE),
      ];
    case 'working':
      return [
        line('Stage', agent.currentStageId ?? NOT_APPLICABLE),
        line('Elapsed', formatDurationMs(agent.elapsedMs)),
      ];
    case 'waiting-tool':
      return [
        line('Tool', agent.waitingOnTool ?? 'unnamed tool'),
        line('Timeout', 'per tool policy', agent.stateExplanation),
      ];
    case 'waiting-human':
      return [line('Requested decision', agent.waitingOnDecision ?? 'unspecified', agent.stateExplanation)];
    case 'blocked':
      return [
        line('Reason', agent.blockedReason ?? agent.stateExplanation),
        line('Recovery', recoveryHint(agent)),
      ];
    case 'unknown':
      return [
        line('Cached state', agent.currentStageId ?? 'no stage recorded'),
        line(
          'Stale for',
          formatAgeMs(agent.lastObserved.ageMs),
          'This is the last value observed. It is not being shown as current.',
        ),
      ];
    case 'error':
      return [line('Error', agent.stateExplanation), line('Recovery', recoveryHint(agent))];
    default: {
      const exhaustive: never = agent.state;
      throw new Error(`unhandled display state ${String(exhaustive)}`);
    }
  }
}

/**
 * The agent-card column of §16.3.
 *
 * The card always carries the state word plus the *reason*, because §16.5
 * requires the same information to be available without looking at the picture.
 */
export function agentCardDetails(agent: OfficeAgent): readonly DetailLine[] {
  // The task and the last sighting only when there is one. See the note on the
  // `idle` branch above: reporting a fact's absence is not the same as
  // reporting the fact.
  const common: DetailLine[] = [];
  if (agent.currentTaskId !== null) common.push(line('Task', agent.currentTaskId));
  if (agent.lastObserved.observedAt !== null) {
    common.push(
      line(
        // "Seen", not "heartbeat": an agent is a harness that reports nothing,
        // and this is when the platform last looked and found it running.
        'Last seen',
        formatAgeMs(agent.lastObserved.ageMs),
        `How current that is: ${freshnessLabel(agent.lastObserved.freshness)}.`,
      ),
    );
  }
  return [...teamOverviewDetails(agent), ...common, line('Why', agent.stateExplanation)];
}

/**
 * A concrete next action.
 *
 * §16.3 requires "reason and recovery" rather than a reason alone, and AC-017
 * requires one specific next decision instead of "needs help".
 */
export function recoveryHint(agent: OfficeAgent): string {
  switch (agent.state) {
    case 'blocked':
      return agent.waitingOnDecision === null
        ? 'Open the run, resolve the blocker, then retry the stage.'
        : `Record the human decision "${agent.waitingOnDecision}" on the task.`;
    case 'error':
      return 'Retry the failing source from the health banner; the attempt is not resumed automatically.';
    case 'unknown':
      return 'Check the source connection. Nothing is retried until the source reports again.';
    case 'waiting-human':
      return 'Answer the requested decision in Policies & Approvals.';
    default:
      return 'No action required.';
  }
}

/**
 * The Office column of §16.3, as a badge the canvas draws and the DOM mirrors.
 *
 * `text` is drawn on the canvas *and* written into the DOM, so the badge is not
 * a picture that a screen reader cannot reach (AC-027).
 */
export interface OfficeBadge {
  readonly text: string;
  readonly tone: Tone;
  readonly icon: string;
  readonly iconLabel: string;
  readonly animates: boolean;
  readonly explanation: string;
}

export function officeBadge(agent: OfficeAgent): OfficeBadge {
  const spec = displaySpec(agent.state);
  return {
    text: spec.badgeText,
    tone: spec.tone,
    icon: spec.icon,
    iconLabel: spec.iconLabel,
    animates: spec.officeAnimates,
    explanation: agent.stateExplanation,
  };
}

/**
 * Scene-wide check that AC-026 is being honoured.
 *
 * Returns the roles that would be a lie if rendered: a role shown as `idle`
 * *while an attempt of its is still in flight* and we have not seen that attempt
 * recently. That role is not idle — it is unobservable, and `deriveAgentState`
 * is supposed to have said so. This is the independent check on that, asserted
 * before painting so the failure surfaces as a loud banner rather than a calm
 * empty desk.
 *
 * Both qualifications are load-bearing, and each was learned by getting it
 * wrong. Without *any* — demanding freshness of every role — a genuinely idle
 * role has nothing to be fresh about, `idle` became unreachable, and a
 * contract-violation banner sat across a working Console. Without **in flight**,
 * a role whose stage is waiting for the board to confirm a transition holds a
 * *finished* attempt, and the check fired thirty seconds later on work that had
 * already ended. Whether the platform can see the roles at all is a different
 * question, answered once for the whole scene by execution source freshness.
 */
export function idleClaimsWithStaleSources(scene: OfficeScene): readonly string[] {
  return scene.agents
    .filter((agent) => agent.state === 'idle' && agent.attemptInFlight && agent.lastObserved.freshness !== 'fresh')
    .map((agent) => agent.roleInstanceId);
}

/** How many roles sit in each matrix row; drives the overview's summary strip. */
export function stateHistogram(scene: OfficeScene): ReadonlyMap<AgentDisplayState, number> {
  const counts = new Map<AgentDisplayState, number>();
  for (const state of MATRIX_ORDER) {
    counts.set(state, 0);
  }
  for (const agent of scene.agents) {
    counts.set(agent.state, (counts.get(agent.state) ?? 0) + 1);
  }
  return counts;
}
